import React from 'react';

function parseEnumerations(text) {
  const lines = text.split(/\r?\n/);
  const out = {};
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const parts = line.split("|").map(p => p.replace(/^'/, "").replace(/'$/, ""));
    const [, elem, name, code, desc] = parts;
    if (!elem || !code) continue;
    if (!out[elem]) out[elem] = { name, codes: [], byCode: {} };
    out[elem].codes.push({ value: code, label: desc });
    out[elem].byCode[code] = desc;
  }
  return out;
}

function parseList(json, name) {
  const codes = (json?.DefinedList?.Codes?.Code) || [];
  const out = { name, codes: [], byCode: {} };
  for (const c of codes) {
    const value = c.Value?.Value || c.Value;
    const label = c.SuggestedLabel || c.SourceLabel;
    if (!value) continue;
    out.codes.push({ value, label, category: c.Category || "" });
    out.byCode[value] = label;
  }
  return out;
}

const LIST_FILES = [
  ["CauseOfInjury",            "/epcr-data/lists/CauseOfInjury.json"],
  ["EnvironmentalFoodAllergy", "/epcr-data/lists/EnvironmentalFoodAllergy.json"],
  ["Impression",               "/epcr-data/lists/Impression.json"],
  ["IncidentLocationType",     "/epcr-data/lists/IncidentLocationType.json"],
  ["MedicalSurgicalHistory",   "/epcr-data/lists/MedicalSurgicalHistory.json"],
  ["Medication",               "/epcr-data/lists/Medication.json"],
  ["MedicationAllergy",        "/epcr-data/lists/MedicationAllergy.json"],
  ["PatientActivity",          "/epcr-data/lists/PatientActivity.json"],
  ["Procedure",                "/epcr-data/lists/Procedure.json"],
  ["Protocols",                "/epcr-data/lists/Protocols.json"],
  ["Symptom",                  "/epcr-data/lists/Symptom.json"],
];

// Elements whose options come from the large external JSON lists rather than
// the element spec (the spec contains no enumeration facets for these).
const ELEMENT_LIST_MAP = {
  "eSituation.04": null,
  "eSituation.09": "Symptom",
  "eSituation.11": "Impression",
  "eSituation.12": "Impression",
  "eMedications.03": "Medication",
  "eProcedures.03":  "Procedure",
  "eHistory.06":  "MedicationAllergy",
  "eHistory.07":  "EnvironmentalFoodAllergy",
  "eHistory.08":  "MedicalSurgicalHistory",
  "eHistory.12":  "Medication",
  "eHistory.16":  "EnvironmentalFoodAllergy",
  "eHistory.17":  "MedicalSurgicalHistory",
  "eScene.09":    "IncidentLocationType",
  "eInjury.01":   "CauseOfInjury",
  "eProtocols.01": "Protocols",
};

export const DataContext = React.createContext(null);

// Ensure every repeatable group (maxOccurs="M") that currently holds a single
// plain object is normalised to a one-element array.  NEMSIS exporters often
// omit the array wrapper when only one record exists; this makes the in-memory
// shape uniform so that index-based paths always work.
function normalizeRepeatableGroups(report, groupSpecs) {
  const pcr = report?.EMSDataSet?.Header?.PatientCareReport;
  if (!pcr || !groupSpecs) return report;
  const result = JSON.parse(JSON.stringify(report));
  const pcrCopy = result.EMSDataSet.Header.PatientCareReport;
  for (const spec of Object.values(groupSpecs)) {
    if (spec.maxOccurs !== 'M') continue;
    const parts = spec.path.split('/');
    let obj = pcrCopy;
    for (let i = 0; i < parts.length - 1; i++) {
      if (obj == null) { obj = null; break; }
      obj = obj[parts[i]];
    }
    if (obj == null) continue;
    const key = parts[parts.length - 1];
    const val = obj[key];
    if (val != null && !Array.isArray(val) && typeof val === 'object') {
      obj[key] = [val];
    }
  }
  return result;
}

export function DataProvider({ children }) {
  const [state, setState] = React.useState({
    loaded: false, enums: {}, lists: {}, specs: {}, paths: {}, groups: {}, report: null,
    currentReport: "report_empty.json",
  });

  // Keep a stable ref to group-specs so loadReport can access them without
  // being recreated every time groups changes.
  const groupsRef = React.useRef({});

  React.useEffect(() => {
    (async () => {
      try {
        const [enumText, reportJson, specsJson, pathsJson, groupsJson, ...listJsons] = await Promise.all([
          fetch("/epcr-data/enumerations.txt").then(r => r.text()),
          fetch("/epcr-data/report_empty.json").then(r => r.json()),
          fetch("/epcr-data/element-specs.json").then(r => r.json()),
          fetch("/epcr-data/section-specs.json").then(r => r.json()),
          fetch("/epcr-data/group-specs.json").then(r => r.json()),
          ...LIST_FILES.map(([, p]) => fetch(p).then(r => r.json())),
        ]);
        const enums = parseEnumerations(enumText);
        const lists = {};
        LIST_FILES.forEach(([name], i) => {
          lists[name] = parseList(listJsons[i], name);
        });
        groupsRef.current = groupsJson;
        const normalizedReport = normalizeRepeatableGroups(reportJson, groupsJson);
        setState(s => ({ ...s, loaded: true, enums, lists, specs: specsJson, paths: pathsJson, groups: groupsJson, report: normalizedReport }));
      } catch (e) {
        console.error("Data load error:", e);
        setState(s => ({ ...s, loaded: true, enums: {}, lists: {}, specs: {}, paths: {}, groups: {}, report: null, error: String(e) }));
      }
    })();
  }, []);

  const loadReport = React.useCallback(async (filename) => {
    try {
      const reportJson = await fetch(`/epcr-data/${filename}`).then(r => r.json());
      const normalizedReport = normalizeRepeatableGroups(reportJson, groupsRef.current);
      setState(s => ({ ...s, report: normalizedReport, currentReport: filename }));
    } catch (e) {
      console.error("Report load error:", e);
    }
  }, []);

  const setReportPath = React.useCallback((path, value) => {
    setState(s => {
      if (!s.report) return s;
      const next = JSON.parse(JSON.stringify(s.report));
      let obj = next.EMSDataSet.Header.PatientCareReport;
      const parts = path.split("/");
      for (let i = 0; i < parts.length - 1; i++) {
        if (!(parts[i] in obj) || obj[parts[i]] == null || typeof obj[parts[i]] !== "object") {
          obj[parts[i]] = {};
        }
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return { ...s, report: next };
    });
  }, []);

  const value = React.useMemo(() => ({
    ...state,
    loadReport,
    setReportPath,

    // Return the compiled spec for a NEMSIS element number, or null.
    spec: (elem) => state.specs[elem] || null,

    // Return the canonical JSON path for a NEMSIS element number, or null.
    // Derived from section specification XMLs; e.g. "eDisposition.27" →
    // "eDisposition/eDisposition.IncidentDispositionGroup/eDisposition.27".
    path: (elem) => state.paths[elem] || null,

    // Return group spec by group number, or null.
    // e.g. "eMedications.MedicationGroup" → { section, path, maxOccurs, parentGroup? }
    groupSpec: (groupNumber) => state.groups[groupNumber] || null,

    // Resolve a stored code (or array of codes) to a human-readable label.
    // Checks spec enumerations → spec NV/PN → external lists → enumerations.txt.
    label: (elem, code) => {
      if (code == null || code === "") return "";
      if (Array.isArray(code)) return code.map(c => resolveLabel(elem, c, state)).filter(Boolean);
      return resolveLabel(elem, code, state);
    },

    // Return the ordered option list for a picker.
    // Priority: spec enumerations → external JSON list → enumerations.txt.
    options: (elem) => {
      const spec = state.specs[elem];
      if (spec?.enumerations?.length > 0) return spec.enumerations;
      const listName = ELEMENT_LIST_MAP[elem];
      if (listName && state.lists[listName]) return state.lists[listName].codes;
      return state.enums[elem]?.codes || [];
    },
  }), [state, setReportPath]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

function resolveLabel(elem, code, state) {
  if (code == null || code === "") return "";
  const spec = state.specs[elem];
  // Check spec enumerations
  if (spec?.enumerations?.length) {
    const match = spec.enumerations.find(e => e.value === String(code));
    if (match) return match.label;
  }
  // Check NV codes (7701xxx) — shared across elements but listed per-element in spec
  if (spec?.nv?.length) {
    const match = spec.nv.find(e => e.code === String(code));
    if (match) return match.label;
  }
  // Check PN codes (8801xxx)
  if (spec?.pn?.length) {
    const match = spec.pn.find(e => e.code === String(code));
    if (match) return match.label;
  }
  // Fall back to external list
  const listName = ELEMENT_LIST_MAP[elem];
  if (listName && state.lists[listName]?.byCode[code]) return state.lists[listName].byCode[code];
  // Fall back to enumerations.txt
  if (state.enums[elem]?.byCode[code]) return state.enums[elem].byCode[code];
  return String(code);
}

export function useData() { return React.useContext(DataContext); }

export function useField(path) {
  const data = useData();
  if (!data?.report) return null;
  let obj = data.report.EMSDataSet.Header.PatientCareReport;
  const parts = path.split("/");
  for (const p of parts) {
    if (obj == null) return null;
    obj = obj[p];
  }
  return obj;
}
